/**
 * TermsIt - i termini di servizio, in italiano
 * ---------------------------------------------
 * The Italian half of the terms. `TermsEn.jsx` is the English half and
 * `Terms.jsx` picks between them; the page furniture - heading, banner, last
 * updated - is shared and lives there.
 *
 * THE TWO FILES MUST SAY THE SAME THING. Change one and change the other in
 * the same commit, and bump TERMS_VERSION in backend/config/features.js.
 * These are not UI strings, which is why they are not in the dictionaries: a
 * legal document is read as a document, and a translator needs to see the
 * whole thing rather than four hundred keys.
 *
 * Neither version is marked as the authoritative one. Which governs if the
 * two ever disagree is question 13 in LEGAL_REVIEW.md, and it is the lawyer's
 * to answer - see the note in Terms.jsx.
 *
 * WRITTEN WITH "TU", NOT "LEI", and that is a deliberate departure from the
 * consent emails, which are formal. The English is deliberately plain because
 * a sixteen-year-old has to be able to read it and the Garante expects that of
 * a service aimed at minors. An Italian translation into "l'Utente si impegna
 * a..." would be a more formal document than the one it translates, and would
 * fail the same test. Plain does not mean vague: every defined term is still
 * defined in section 1.
 */

import { Link } from 'react-router-dom';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
const SAFETY_EMAIL = 'safety@choreconnect.org';

export default function TermsIt() {
  return (
    <>
      <div className="card">
        <h2 style={{ fontSize: 'var(--text-h3)' }}>In breve</h2>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          {APP_NAME} mette in contatto chi ha bisogno di un piccolo lavoro con studenti che
          vogliono farlo. Non siamo un datore di lavoro, non verifichiamo l&rsquo;identità di
          nessuno, non sorvegliamo il lavoro e non gestiamo il denaro. L&rsquo;accordo per fare un
          lavoretto è tra voi due. Sii onesto, sii prudente, e dicci se qualcosa va storto.
        </p>
        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          Il resto di questa pagina è il dettaglio. Conta - ti chiediamo di leggerlo.
        </p>
      </div>

      <hr className="divider" />

      {/* ============================================================= */}
      <h2>Chi gestisce {APP_NAME}</h2>
      <p>
        {APP_NAME} è gestito da <strong>Evelyn Catelijne Brouwer</strong>, Via Guglielmo Marconi
        249, 21027 Ispra (VA), Italia. Dietro non c&rsquo;è nessuna società. Puoi scrivere a{' '}
        <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a> per qualsiasi cosa riguardi questa
        pagina, e l&rsquo;informativa sulla privacy dice lo stesso sui tuoi dati.
      </p>

      {/* ============================================================= */}
      <h2>1. Cosa significano le parole</h2>
      <p>
        Questi termini usano alcune parole in un senso preciso. Ovunque compaiano, significano
        questo.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>{APP_NAME}</strong>, <strong>noi</strong> - il sito choreconnect.org e il
          servizio che offre.
        </li>
        <li>
          <strong>Utente</strong>, <strong>tu</strong> - chiunque abbia un account.
        </li>
        <li>
          <strong>Committente</strong> - un utente adulto che pubblica un lavoretto.
        </li>
        <li>
          <strong>Studente</strong> - un utente di almeno 16 anni che frequenta una scuola che
          supportiamo e può candidarsi ai lavoretti.
        </li>
        <li>
          <strong>Lavoretto</strong> - un singolo lavoro specifico che un committente descrive sul
          sito, con una data, un luogo e un compenso.
        </li>
        <li>
          <strong>Disponibilità</strong> - uno studente che indica in cosa può dare una mano e in
          quali momenti della settimana è di solito libero.
        </li>
        <li>
          <strong>Riserva</strong> - un secondo studente che il committente accetta nel caso il
          primo non possa andare.
        </li>
        <li>
          <strong>Contenuti</strong> - tutto quello che scrivi, carichi o invii tramite il sito:
          descrizioni dei lavoretti, disponibilità, testo del profilo, fotografie, recensioni e
          messaggi.
        </li>
        <li>
          <strong>Moderatore</strong> - una persona a cui abbiamo dato la possibilità di leggere le
          segnalazioni, agire di conseguenza e rimuovere contenuti.
        </li>
        <li>
          <strong>Lavoro pericoloso</strong> - definito alla sezione 8.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>2. Chi può usare {APP_NAME}</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Gli studenti devono avere almeno 16 anni</strong>, l&rsquo;età minima per
          lavorare in Italia. Chiediamo la tua data di nascita e prendiamo per buona la tua
          risposta - vedi la sezione 3. Dopo la registrazione non si può cambiare dal tuo account;
          se è sbagliata, avvisaci.
        </li>
        <li>
          <strong>Gli studenti minorenni hanno bisogno che un genitore o tutore confermi via
          email</strong> prima di candidarsi, pubblicare la propria disponibilità o essere
          prenotati. Fino ad allora non succede nulla. Dal diciottesimo compleanno dello studente
          non serve più.
        </li>
        <li>
          <strong>I committenti devono avere almeno 18 anni.</strong>
        </li>
        <li>
          <strong>Gli studenti devono frequentare una scuola che supportiamo</strong> e avere il
          suo codice di accesso.
        </li>
        <li>
          <strong>I committenti, e gli studenti che si registrano da maggiorenni, devono
          confermare il proprio indirizzo email</strong> prima di pubblicare un lavoretto,
          candidarsi, pubblicare la propria disponibilità o chiedere a uno studente di un orario.
          Prima di questo puoi comunque guardarti intorno.
        </li>
        <li>
          <strong>Un solo account a testa</strong>, a tuo nome, con dati veri.
        </li>
      </ul>
      <p>
        Sei responsabile di quello che succede tramite il tuo account e di tenere la password per
        te. Dicci se pensi che qualcun altro vi sia entrato.
      </p>

      {/* ============================================================= */}
      <h2>3. Cosa siamo e cosa non siamo</h2>
      <p>
        <strong>{APP_NAME} è un servizio di presentazione e nient&rsquo;altro.</strong> Mostriamo
        chi ha bisogno di un lavoro accanto agli studenti che vogliono farlo. Tutto quello che
        viene dopo la presentazione è tra voi due.
      </p>

      <h3>Non verifichiamo nessuno</h3>
      <p>Questo è il paragrafo più importante della pagina. Ti chiediamo di non saltarlo.</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Non verifichiamo l&rsquo;identità di nessuno.</strong> Non chiediamo né
          controlliamo alcun documento. Un nome su questo sito è un nome che qualcuno ha digitato.
        </li>
        <li>
          <strong>Non effettuiamo controlli sui precedenti penali</strong> di nessuno, di nessun
          tipo, mai.
        </li>
        <li>
          <strong>Non verifichiamo l&rsquo;età.</strong> L&rsquo;età di uno studente si ricava
          dalla data di nascita che ci ha dichiarato.
        </li>
        <li>
          <strong>Non verifichiamo gli indirizzi</strong>, né che un luogo sia come è stato
          descritto, né che sia sicuro.
        </li>
        <li>
          <strong>Non controlliamo referenze</strong>, qualifiche, assicurazioni o il diritto al
          lavoro.
        </li>
        <li>
          <strong>Non verifichiamo che l&rsquo;indirizzo email di un genitore appartenga davvero a
          un genitore.</strong> Inviamo un messaggio all&rsquo;indirizzo che lo studente ci
          fornisce e ci basiamo sulla risposta.
        </li>
        <li>
          <strong>Le recensioni e le valutazioni sono opinioni scritte dagli utenti.</strong> Non
          sono verificate da noi e non sono una raccomandazione.
        </li>
      </ul>
      <p>
        <strong>Considera ogni utente uno sconosciuto, perché è esattamente quello che è.</strong>{' '}
        Se non faresti una cosa con uno sconosciuto, non farla per via di qualcosa che hai letto
        qui.
      </p>

      <h3>Non sorvegliamo il lavoro</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          Non siamo presenti. Non ispezioniamo nessun luogo di lavoro, né prima, né durante, né
          dopo.
        </li>
        <li>
          Non verifichiamo che un lavoretto sia sicuro, lecito, adatto a un minore o descritto in
          modo accurato.
        </li>
        <li>Non verifichiamo che il lavoro sia stato svolto, o svolto bene.</li>
        <li>Non verifichiamo che qualcuno sia stato pagato.</li>
        <li>
          Non verifichiamo se qualcuno abbia adempiuto agli obblighi fiscali, previdenziali, INPS,
          INAIL o assicurativi - vedi la sezione 9.
        </li>
      </ul>

      <h3>Non siamo</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          un datore di lavoro, un&rsquo;agenzia per il lavoro o un intermediario di manodopera
        </li>
        <li>parte di alcun accordo che stipuli con un altro utente</li>
        <li>rappresentanti di nessuno dei due</li>
        <li>coinvolti nel pagamento - il denaro viene gestito direttamente tra di voi</li>
        <li>
          garanti della condotta, dell&rsquo;onestà, della capacità o della sicurezza di nessuno
        </li>
      </ul>
      <p>
        <strong>L&rsquo;accordo per svolgere un lavoretto è tra il committente e lo
        studente.</strong> Se va storto, è una questione tra voi due - anche se ci farebbe comunque
        piacere saperlo, e la sezione 12 spiega come.
      </p>

      {/* ============================================================= */}
      <h2>4. Cosa non devi fare</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Violare la legge, o usare il sito per organizzare qualcosa di illecito.</li>
        <li>Mentire su chi sei, sulla tua età o su cosa comporta un lavoro.</li>
        <li>Usare l&rsquo;account di qualcun altro, o lasciare che altri usino il tuo.</li>
        <li>
          Pubblicare contenuti offensivi, minacciosi, discriminatori, sessuali o molesti.
        </li>
        <li>
          Contattare uno studente per scopi diversi da un lavoretto su questo sito, o chiedergli
          contatti personali per spostare la conversazione dove nessuno può vederla.
        </li>
        <li>Chiedere a uno studente di svolgere lavori pericolosi (sezione 8).</li>
        <li>
          Pubblicare dati personali di altri, o la fotografia di un&rsquo;altra persona.
        </li>
        <li>Fare pubblicità, reclutare, o usare il sito per vendere qualcosa.</li>
        <li>
          Raccogliere dati dal sito in modo automatico, tentare di aggirarne la sicurezza o
          interferire con il suo funzionamento.
        </li>
        <li>Pubblicare qualcosa che non hai il diritto di pubblicare.</li>
      </ul>

      {/* ============================================================= */}
      <h2>5. I tuoi contenuti</h2>
      <p>
        <strong>Quello che scrivi resta tuo.</strong> Pubblicandolo ci dai il permesso di
        conservarlo, mostrarlo alle persone a cui è destinato e custodirlo come descritto nella
        nostra <Link to="/privacy">informativa sulla privacy</Link>. Questo permesso è gratuito e
        dura quanto ci serve per far funzionare il sito e conservare i registri descritti lì.
        Esiste perché possiamo mostrare il tuo lavoretto agli studenti, o la tua recensione agli
        altri utenti, e per nient&rsquo;altro: non lo vendiamo, non lo cediamo in licenza e non lo
        usiamo per fare pubblicità.
      </p>
      <p>
        <strong>Sei responsabile di quello che pubblichi</strong>, e confermi di avere il diritto
        di pubblicarlo e che è accurato.
      </p>
      <p>
        <strong>Le recensioni</strong> devono essere oneste, riferite a un lavoro realmente svolto,
        e riguardare il lavoro più che la persona. Niente offese e niente dati personali.
      </p>
      <p>
        <strong>Su un lavoretto è sempre lo studente a iniziare la conversazione.</strong> Uno
        studente può fare una domanda su un lavoretto aperto alle candidature, e le due persone di
        un lavoretto concordato possono scriversi al riguardo. Chi pubblica un lavoretto non può
        mai scrivere per primo a uno studente.
      </p>
      <p>
        <strong>La disponibilità funziona al contrario, e proprio per questo è limitata.</strong>{' '}
        A uno studente che pubblica quando è libero un adulto può rivolgere <strong>una sola
        domanda</strong>, e nulla di più se lo studente non risponde. È lo studente a decidere se
        rispondere: la risposta è ciò che apre la conversazione, e ignorarla lascia l&rsquo;adulto
        senza alcun modo di scrivere ancora. Un adulto può avere al massimo cinque domande senza
        risposta in tutto il sito. Ritirare la disponibilità chiude la conversazione.
      </p>
      <p>
        <strong>I messaggi non sono privati nei nostri confronti.</strong> Vengono conservati, non
        vengono mai cancellati, e un moderatore può leggere una conversazione se viene segnalata o
        se il nostro sistema rileva uno scambio di contatti. L&rsquo;informativa sulla privacy
        spiega esattamente quando. È una scelta voluta: è ciò che rende possibile approfondire una
        segnalazione su qualcosa detto in privato.
      </p>

      {/* ============================================================= */}
      <h2>6. Svolgere un lavoretto</h2>
      <h3>Tutti</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Concordate il lavoro, l&rsquo;orario e il compenso prima di cominciare.</li>
        <li>Presentati quando hai detto che lo avresti fatto, o avvisa per tempo.</li>
        <li>Tratta l&rsquo;altra persona con rispetto.</li>
      </ul>

      <h3>Committenti</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          Descrivi il lavoro onestamente, compreso tutto ciò che è pesante, sporco o scomodo.
        </li>
        <li>
          Indica l&rsquo;indirizzo reale dove si svolge il lavoro. Lo vedono solo lo studente che
          accetti e la riserva.
        </li>
        <li>
          Metti a disposizione un luogo di lavoro sicuro, e di&rsquo; cosa ti aspetti che porti.
        </li>
        <li>Paga quanto concordato, quando concordato.</li>
        <li>
          <strong>Ricorda che potresti avere a che fare con un sedicenne.</strong> Non chiedere a
          uno studente di restare da solo con te in un posto da cui non può andarsene, non offrire
          alcolici, e non chiedergli di fare nulla che non chiederesti davanti a un suo genitore.
        </li>
      </ul>

      <h3>Studenti</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Candidati solo a lavori che sai davvero fare in sicurezza.</li>
        <li>
          <strong>Di&rsquo; a qualcuno a casa dove vai e quando torni.</strong>
        </li>
        <li>Avvisa con almeno 24 ore di anticipo se non ce la fai.</li>
      </ul>

      {/* ============================================================= */}
      <h2>7. Puoi sempre dire di no</h2>
      <p>
        Questa sezione è per gli studenti, ed è la cosa più importante di questa pagina per te.
      </p>
      <p>
        <strong>Niente su {APP_NAME} ti obbliga a entrare in una situazione di cui non ti
        fidi.</strong> Né il fatto che ti sei candidato, né che hai accettato, né che qualcuno ti
        sta aspettando, né la regola delle 24 ore.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Non sei obbligato a entrare.</strong> Se il posto, la persona o la situazione non
          sono come te li aspettavi quando arrivi - più gente di quanta te ne avessero detta, un
          indirizzo diverso, qualsiasi cosa non ti convinca - gira i tacchi e vai via. Non sei mai
          obbligato a bussare.
        </li>
        <li>
          <strong>Puoi andartene a metà</strong>, in qualsiasi momento, senza finire e senza dare
          spiegazioni.
        </li>
        <li>
          <strong>Non devi mai una spiegazione a chi ha pubblicato il lavoretto</strong>, e non sei
          tenuto a restare per educazione. L&rsquo;educazione non vale la tua sicurezza, e chiunque
          sia una persona perbene preferirebbe che tu te ne andassi. Dire a <em>noi</em> cosa è
          successo è un&rsquo;altra cosa, e la sezione 7 qui sotto spiega quando lo chiediamo: non
          viene mai mostrato a loro.
        </li>
        <li>
          <strong>Fidati della sensazione.</strong> Non devi saper indicare cosa non va, e non devi
          neanche avere ragione.
        </li>
      </ul>
      <p>
        <strong>Usa il pulsante &ldquo;Non mi sento al sicuro&rdquo; sulla tua bacheca.</strong>{' '}
        Funziona anche dentro le 24 ore, e a differenza di una cancellazione tardiva non lascia
        alcuna traccia sul tuo storico. Quando lo usi:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Non ti viene contato contro.</strong> Non è una cancellazione, e non compare
          nulla sul tuo profilo o nel tuo storico.
        </li>
        <li>
          <strong>All&rsquo;altra persona non viene detto che hai segnalato qualcosa.</strong> Le
          viene solo detto che il lavoretto è di nuovo libero. Lo facciamo così nel caso tu sia
          ancora nei paraggi.
        </li>
        <li>
          <strong>Devi dirci cosa è successo</strong> - qualche frase, prima di andartene. Un
          moderatore la legge in giornata ed è tutto quello che ha per capire. Non viene mai
          mostrata a chi ha pubblicato il lavoretto.
        </li>
        <li>
          <strong>Serve se non ti senti al sicuro, non se hai cambiato idea.</strong> Usarlo per
          uscire da un lavoretto che semplicemente non ti va è una violazione di questi termini e
          può portare alla sospensione del tuo account. Se non puoi venire, annulla il lavoretto e
          spiega perché: è a questo che serve la cancellazione, e nessuno si mette nei guai.
        </li>
      </ul>
      <p>
        <strong>Se sei in pericolo in questo momento, chiama prima il 112.</strong> Tutto il resto
        può aspettare.
      </p>
      <p className="text-sm text-muted">
        Niente di tutto questo ti rende responsabile della tua sicurezza in un modo che scagioni
        qualcun altro. Un committente che ti mette in quella situazione ha violato questi termini,
        qualunque cosa tu abbia fatto in risposta.
      </p>

      {/* ============================================================= */}
      <h2>8. Lavori pericolosi</h2>
      <p>
        <strong>Non devi chiedere a uno studente di svolgere lavori pericolosi, e uno studente non
        deve accettarli.</strong> La legge italiana tutela i minori di 18 anni da alcuni tipi di
        lavoro - vedi il D.Lgs. 345/1999 e l&rsquo;allegato al D.Lgs. 81/2008. Poiché su
        &ldquo;pericoloso&rdquo; è facile discutere, ecco cosa significa su questo sito. Comprende,
        senza limitarsi a:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          lavori in quota - scale, tetti, grondaie, qualsiasi cosa al di sopra dell&rsquo;altezza a
          cui si arriva in piedi
        </li>
        <li>utensili elettrici, motoseghe, trattorini tosaerba o macchinari industriali</li>
        <li>
          prodotti chimici diversi dai normali detergenti per la casa usati secondo
          l&rsquo;etichetta - pesticidi, solventi, acidi o basi forti
        </li>
        <li>
          lavori elettrici, sul gas o idraulici che vadano oltre il cambio di una lampadina o di
          una pila
        </li>
        <li>
          sollevare pesi che richiederebbero due persone, o sollevamenti pesanti ripetuti
        </li>
        <li>lavori con fiamme, superfici calde o qualsiasi cosa che possa ustionare</li>
        <li>demolizioni, edilizia o qualsiasi intervento strutturale</li>
        <li>manipolazione di rifiuti taglienti, sanitari o pericolosi</li>
        <li>lavori con animali che potrebbero ragionevolmente ferire qualcuno</li>
        <li>guidare qualsiasi veicolo</li>
        <li>lavori in spazi confinati, o dove non si possa uscire da soli</li>
        <li>lavoro notturno, cioè tra le 22:00 e le 06:00</li>
      </ul>
      <p>
        <strong>Nel dubbio, no.</strong> Un committente che chiede una di queste cose sta violando
        questi termini: rimuoveremo il lavoretto e potremmo chiudere l&rsquo;account. Uno studente a
        cui venga chiesto di farlo deve rifiutare, andarsene e dircelo.
      </p>

      {/* ============================================================= */}
      <h2>9. Cancellazioni</h2>
      <p>
        Uno studente che ha accettato un lavoro dovrebbe cancellare con almeno 24 ore di anticipo
        ogni volta che può. <strong>Entro le 24 ore puoi comunque cancellare, ma devi dire
        perché</strong>, e quello che scrivi viene mostrato a chi ha pubblicato il lavoretto. Resta
        comunque registrato come cancellazione tardiva. Far mancare ripetutamente la propria parola
        può costarti l&rsquo;account.
      </p>
      <p>
        Un committente che cancella dovrebbe avvisare con il maggior preavviso possibile. A uno
        studente che ha viaggiato per un lavoro cancellato senza preavviso si deve almeno la
        cortesia di una spiegazione.
      </p>

      {/* Aggiunto il 24 agosto 2026 insieme al blocco delle modifiche su un
          lavoretto già accettato. Entrambe le metà sono applicate dal codice -
          LOCKED_ONCE_ASSIGNED in backend/routes/chores.js rifiuta la modifica,
          e datesMovedAfter in backend/models/Chore.js è tutto il diritto dello
          studente. Non ammorbidire questo testo senza cambiare quelli. */}
      <h3>Modificare un lavoretto che qualcuno ha già accettato</h3>
      <p>
        Una volta che uno studente ha accettato, <strong>il lavoretto è fissato</strong>. Il
        committente non può più cambiare il titolo, la descrizione, la categoria, il compenso,
        l&rsquo;indirizzo o la durata: lo studente ha valutato quel lavoretto e ha accettato quello,
        e un lavoretto diverso richiede un accordo diverso. Se il lavoro è cambiato davvero, il
        committente annulla e ne pubblica uno nuovo.
      </p>
      <p>
        <strong>Il giorno e l&rsquo;ora si possono ancora spostare</strong>, perché i programmi
        cambiano ed è meglio farlo sul sito che in privato. Ma spostarli ha un costo:{' '}
        <strong>da quel momento lo studente può annullare in qualsiasi momento, senza alcun
        preavviso, e la cosa non viene conteggiata a suo carico.</strong> Aveva accettato un giorno
        preciso, e sentirsi chiedere di un altro è una nuova domanda a cui ha il diritto di
        rispondere di no. Entrambe le persone vengono avvisate quando succede.
      </p>

      <h3>Lavoretti che durano più giorni</h3>
      <p>
        Un lavoretto può coprire più di un giorno - un cane portato a spasso ogni mattina per una
        settimana, o il babysitting ogni mercoledì e sabato. Una prenotazione di questo tipo può
        estendersi fino a <strong>tre mesi</strong> e contenere al massimo{' '}
        <strong>100 appuntamenti</strong>. Su {APP_NAME} niente si ripete senza una data di fine.
      </p>
      <p>
        Il compenso indicato è <strong>per appuntamento</strong>, e accanto viene mostrato il
        totale dell&rsquo;intera prenotazione. Lo studente vede tutte le date prima di accettare, e
        accetta l&rsquo;insieme, non una parte.
      </p>
      <p>
        <strong>Entrambe le parti possono rinunciare a una singola giornata senza far decadere
        l&rsquo;intera prenotazione.</strong> Lo studente deve farlo con almeno 24 ore di anticipo
        su quella giornata, lo stesso preavviso di qualsiasi altro lavoro; il committente può farlo
        in qualsiasi momento, perché rinunciare a una giornata libera lo studente anziché lasciarlo
        scoperto. All&rsquo;altra persona viene detto quale giornata e perché. Il resto della
        prenotazione prosegue invariato.
      </p>
      <p>
        Rinunciare a una giornata non equivale ad abbandonare il lavoro. Uno studente che vuole
        chiudere l&rsquo;intero accordo lo cancella nel modo ordinario, secondo le regole qui
        sopra - e uno studente che non si sente al sicuro può sempre andarsene subito, in qualsiasi
        momento della prenotazione, secondo la sezione 7.
      </p>

      {/* ============================================================= */}
      <h2>10. Pagamenti, tasse e contributi</h2>
      <p>
        <strong>Il pagamento è concordato e gestito direttamente tra committente e
        studente.</strong>{' '}
        Non lo tratteniamo mai, non lo trasferiamo, non lo garantiamo e non ne prendiamo una quota.
        Concordate l&rsquo;importo prima che il lavoro cominci.
      </p>
      <p>
        <strong>Non interveniamo in alcuna controversia sul pagamento</strong> e non possiamo
        recuperare denaro per te, tenerlo in deposito o stabilire chi ha ragione.
      </p>
      <p>
        In Italia il lavoro domestico occasionale può essere pagato in modo regolare tramite il{' '}
        <em>Libretto Famiglia</em> dell&rsquo;INPS, che copre esattamente questo tipo di
        attività - giardinaggio, pulizie, assistenza ai bambini, ripetizioni - e comprende la
        copertura INAIL per lo studente.{' '}
        <strong>Ne consigliamo vivamente l&rsquo;uso. Non lo imponiamo e non lo
        verifichiamo.</strong>
      </p>
      <p>
        <strong>L&rsquo;adempimento di qualsiasi obbligo fiscale, previdenziale, INPS, INAIL,
        assicurativo o relativo al diritto al lavoro è interamente a carico del committente e dello
        studente.</strong>{' '}
        Non diamo consulenza in merito, non ce ne assumiamo la responsabilità e non verifichiamo se
        qualcuno vi abbia provveduto. Se hai dubbi, rivolgiti a un commercialista.
      </p>

      <h3>Donazioni</h3>
      <p>
        Un committente maggiorenne può scegliere di fare una donazione per le spese di gestione di{' '}
        {APP_NAME}. Agli studenti non viene mai chiesto, e non possono donare.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Una donazione è un regalo, non il pagamento di qualcosa.</strong> Non compra
          funzioni, distintivi, priorità o considerazione, non viene mai mostrata ad altri e non
          cambia nulla nel modo in cui il sito tratta te o le persone con cui hai a che fare.
        </li>
        <li>
          <strong>È una tantum.</strong> Non si rinnova e non c&rsquo;è nulla da disdire.
        </li>
        <li>
          <strong>Il pagamento avviene tramite Stripe</strong>, che gestisce i dati della tua carta
          e ti invia la ricevuta via email. Noi non vediamo mai la tua carta.
        </li>
        <li>
          <strong>Se doni per errore</strong> - l&rsquo;importo sbagliato, o due volte - scrivici e
          ti rimborseremo. Per il resto, le donazioni non sono rimborsabili.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>11. Moderazione, sospensione e rimozione</h2>
      <p>
        Possiamo, a nostra discrezione e senza che ci venga chiesto, fare quanto segue quando
        riteniamo ragionevolmente che questi termini siano stati violati o che qualcuno sia a
        rischio:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Rimuovere un lavoretto o una disponibilità.</strong> Lo diremo a chi lo ha
          pubblicato, spiegando il motivo.
        </li>
        <li>
          <strong>Nascondere un messaggio</strong> alle due persone di una conversazione. Il
          messaggio in sé viene conservato.
        </li>
        <li>
          <strong>Rimuovere una recensione</strong> che violi la sezione 5.
        </li>
        <li>
          <strong>Sospendere un account per un periodo determinato</strong> - da un giorno a un
          anno. Ti verrà detto perché e quando finisce, e verrai disconnesso da tutti i dispositivi.
        </li>
        <li>
          <strong>Sospendere un account a tempo indeterminato</strong>, quando quanto accaduto è
          abbastanza grave.
        </li>
      </ul>
      <p>
        Di norma spiegheremo e, quando è sicuro e sensato farlo, ti daremo la possibilità di
        rispondere. <strong>Possiamo agire prima e spiegare dopo quando qualcuno potrebbe essere a
        rischio</strong>, e riteniamo che sia l&rsquo;ordine giusto.
      </p>
      <p>
        Se ritieni che una decisione sia sbagliata, rispondi al messaggio che te ne ha informato, o
        scrivi a <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>. Una persona la riesaminerà.
      </p>

      {/* ============================================================= */}
      <h2>12. Disponibilità del sito</h2>
      <p>
        Forniamo {APP_NAME} così com&rsquo;è. <strong>Non promettiamo che sarà disponibile,
        funzionante, ininterrotto, sicuro o privo di errori</strong>, e possiamo modificare,
        sospendere o interrompere qualsiasi sua parte in qualsiasi momento. È gestito da una sola
        persona ed è gratuito.
      </p>
      <p>
        Non garantiamo che quanto si trova sul sito sia accurato, completo o aggiornato -
        comprese le descrizioni dei lavoretti, i profili, le disponibilità, le distanze e le
        recensioni, che provengono tutte dagli utenti.
      </p>

      {/* ============================================================= */}
      <h2>13. Sicurezza e segnalazioni</h2>
      <p>
        <strong>Se qualcuno è in pericolo immediato, chiama il 112.</strong> Fallo per primo e
        contattaci dopo.
      </p>
      <p>
        Altrimenti usa il pulsante Segnala su qualsiasi profilo, lavoretto o conversazione, oppure
        scrivi a <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>. Leggiamo ogni segnalazione, e
        <strong> un moderatore le legge ogni giorno alle 20:00</strong>. Di notte non c&rsquo;è
        nessuno che controlla il sito, ed è per questo che la riga qui sopra sul 112 viene prima.
        Dicci cosa è successo e quando, e ti diremo cosa abbiamo fatto per quanto ci è possibile.
      </p>
      <p>
        Puoi anche <strong>bloccare</strong> un altro utente in qualsiasi momento, senza dare
        spiegazioni e senza che gli venga detto.
      </p>
      <p>
        <strong>&ldquo;Segnala un problema&rdquo; è un’altra cosa, e non serve a questo.</strong> È
        per il sito che non funziona: un pulsante che non fa niente, una pagina che non si carica.
        Arriva alle stesse persone, ma non viene trattato come una segnalazione di sicurezza e non
        entra nella coda come tale. Se qualcuno ti ha messo a disagio, usa il pulsante Segnala qui
        sopra.
      </p>

      {/* ============================================================= */}
      <h2>14. Chiudere il tuo account</h2>
      <p>
        Puoi chiudere il tuo account in qualsiasi momento dalle impostazioni. Un genitore o tutore
        può revocare il proprio consenso in qualsiasi momento, il che impedisce a suo figlio o sua
        figlia di usare il sito per lavorare.
      </p>
      <p>
        Chiudere un account rimuove i tuoi dati personali. Lavoretti, recensioni e messaggi
        restano, con il tuo nome sostituito, perché cancellarli porterebbe via con sé i dati di
        altre persone. L&rsquo;<Link to="/privacy">informativa sulla privacy</Link> lo spiega per
        esteso.
      </p>

      {/* ============================================================= */}
      <h2>15. Responsabilità</h2>
      <p>
        Questa sezione dice di cosa siamo e non siamo responsabili.{' '}
        <strong>Nulla in essa toglie i diritti che hai come consumatore secondo la legge
        italiana.</strong>
      </p>
      <p>
        <strong>Non siamo responsabili di quello che accade tra gli utenti.</strong> Nella misura
        massima consentita dalla legge, questo comprende:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>lesioni, malattia o morte occorse durante o a causa di un lavoretto</li>
        <li>danni, perdita o furto di beni di chiunque</li>
        <li>la qualità, la sicurezza o la liceità di qualsiasi lavoro</li>
        <li>se qualcuno venga pagato, pagato puntualmente o pagato per intero</li>
        <li>qualsiasi cosa qualcuno dica o faccia, sul sito o di persona</li>
        <li>il fatto che qualcuno sia davvero chi ha detto di essere</li>
        <li>
          qualsiasi obbligo fiscale, previdenziale, INPS, INAIL, assicurativo o relativo al diritto
          al lavoro
        </li>
        <li>
          qualsiasi contenuto pubblicato da un utente, comprese cose false, offensive o illecite
        </li>
      </ul>
      <p>
        <strong>Di cosa siamo responsabili.</strong> Siamo responsabili di gestire il sito con
        ragionevole diligenza, di trattare i tuoi dati come descritto nella nostra informativa
        sulla privacy e di dare seguito alle segnalazioni. Se veniamo meno a questo e ciò ti causa
        un danno, ne rispondiamo nei termini previsti dalla legge.
      </p>
      <p>
        <strong>Cosa non può essere limitato, e non lo è.</strong> Nulla in questi termini limita o
        esclude la nostra responsabilità per morte o lesioni personali causate da nostra
        negligenza, per dolo o falsa dichiarazione fraudolenta, per condotta intenzionalmente
        illecita o colpa grave, o per qualsiasi altra cosa che non possa legalmente essere
        limitata. Se una parte di questa sezione dovesse risultare eccedente quanto la legge
        consente, si applica nella misura massima lecita e il resto resta valido.
      </p>

      {/* ============================================================= */}
      <h2>16. Se ci causi un danno</h2>
      <p>
        Se qualcuno avanza una pretesa nei nostri confronti a causa di qualcosa che hai fatto -
        violare questi termini, violare la legge, ferire qualcuno o pubblicare qualcosa che non
        avevi il diritto di pubblicare - accetti di coprire i costi ragionevoli per farvi fronte,
        comprese le spese legali.
      </p>
      <p>
        Questo non si applica quando la pretesa è stata causata da un nostro errore, e non si
        applica a nulla che tu non abbia effettivamente causato.
      </p>

      {/* ============================================================= */}
      <h2>17. Modifiche a questi termini</h2>
      <p>
        Possiamo modificare questi termini. Se una modifica è rilevante te lo diremo e aggiorneremo
        la data in cima alla pagina. Continuare a usare il sito dopo di ciò significa accettare la
        nuova versione; se non la accetti, puoi chiudere il tuo account.
      </p>
      <p>
        <strong>Se una modifica riguarda in modo sostanziale uno studente minorenne, chiediamo di
        nuovo al genitore o al tutore</strong> anziché basarci sulla risposta già data.
      </p>

      {/* ============================================================= */}
      <h2>18. Legge applicabile, foro competente e reclami</h2>
      <p>
        Questi termini sono regolati dalla <strong>legge italiana</strong>.
      </p>
      <p>
        Se sei un consumatore puoi promuovere un&rsquo;azione davanti al giudice del luogo in cui
        risiedi, e nulla qui toglie la tutela della legge del Paese in cui vivi. Diversamente sono
        competenti i tribunali italiani.
      </p>
      <p>
        Prima di rivolgerti a un giudice, ti chiediamo di scriverci: la maggior parte delle cose si
        risolve più in fretta direttamente. Come consumatore potresti anche poter ricorrere a un
        organismo di risoluzione stragiudiziale delle controversie ai sensi del Codice del Consumo.
      </p>

      {/* ============================================================= */}
      <h2>19. Varie</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Se una parte di questi termini non è applicabile</strong>, il resto resta valido.
        </li>
        <li>
          <strong>Se non facciamo valere subito una previsione</strong>, non abbiamo rinunciato al
          diritto di farla valere in seguito.
        </li>
        <li>
          <strong>Non puoi trasferire il tuo account</strong> né i tuoi diritti derivanti da questi
          termini a nessun altro.
        </li>
        <li>
          <strong>Le sezioni 5, 10, 15, 16 e 18 continuano ad applicarsi</strong> dopo la chiusura
          del tuo account.
        </li>
        <li>
          <strong>Questi termini e l&rsquo;informativa sulla privacy costituiscono
          l&rsquo;intero accordo</strong> tra noi riguardo a {APP_NAME}.
        </li>
      </ul>

      <hr className="divider" />

      <p className="text-sm text-muted">
        Vedi anche la nostra <Link to="/privacy">informativa sulla privacy</Link>. Domande su questi
        termini: <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>.
      </p>
    </>
  );
}
